'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';

export interface SDraggableGroupMoveEvent {
  group: string;
  itemKey: string;
  fromListId: string;
  fromIndex: number;
  toListId: string;
  toIndex: number;
}

interface RegisteredList {
  element: HTMLDivElement;
  items: unknown[];
  onChange?: (items: unknown[]) => void;
}

interface GroupContextValue {
  defaultGroup?: string;
  dragging: { group: string; listId: string; index: number; itemKey: string } | null;
  drop: { group: string; listId: string; index: number } | null;
  registerList: (
    group: string,
    listId: string,
    element: HTMLDivElement | null,
    items: unknown[],
    onChange?: (items: unknown[]) => void,
  ) => void;
  startDrag: (
    event: MouseEvent<HTMLDivElement>,
    group: string,
    listId: string,
    index: number,
    itemKey: string,
  ) => void;
}

const DraggableGroupContext = createContext<GroupContextValue | null>(null);

export function useSDraggableGroup() {
  return useContext(DraggableGroupContext);
}

export interface SDraggableGroupProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  onMove: (event: SDraggableGroupMoveEvent) => void;
}

export interface SDraggableProviderProps {
  children: ReactNode;
}

interface SDraggableProviderInternalProps extends SDraggableProviderProps {
  defaultGroup?: string;
  legacyOnMove?: (event: SDraggableGroupMoveEvent) => void;
}

function SDraggableProviderInternal({
  children,
  defaultGroup,
  legacyOnMove,
}: SDraggableProviderInternalProps) {
  const listsRef = useRef(new Map<string, RegisteredList>());
  const legacyOnMoveRef = useRef(legacyOnMove);
  const pointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const [dragging, setDragging] = useState<GroupContextValue['dragging']>(null);
  const [drop, setDrop] = useState<GroupContextValue['drop']>(null);
  const dropRef = useRef(drop);

  legacyOnMoveRef.current = legacyOnMove;
  dropRef.current = drop;

  const registerList = useCallback(
    (
      group: string,
      listId: string,
      element: HTMLDivElement | null,
      items: unknown[],
      onChange?: (items: unknown[]) => void,
    ) => {
      const key = `${group}:${listId}`;
      if (element) listsRef.current.set(key, { element, items, onChange });
      else listsRef.current.delete(key);
    },
    [],
  );

  const startDrag = useCallback(
    (
      event: MouseEvent<HTMLDivElement>,
      group: string,
      listId: string,
      index: number,
      itemKey: string,
    ) => {
      event.preventDefault();
      setDragging({ group, listId, index, itemKey });
      setDrop({ group, listId, index });
    },
    [],
  );

  useEffect(() => {
    if (!dragging) return undefined;

    let autoScrollFrame: number | null = null;

    const findListAtPointer = (clientX: number, clientY: number) => {
      const element = document.elementFromPoint(clientX, clientY);
      const listElement = element?.closest<HTMLElement>('[data-draggable-list-id]');
      const group = listElement?.dataset.draggableGroup;
      const listId = listElement?.dataset.draggableListId;
      const list = group && listId ? listsRef.current.get(`${group}:${listId}`) : undefined;

      return group && listId && list ? { group, listId, list } : null;
    };

    const updateDropTarget = (clientX: number, clientY: number) => {
      const target = findListAtPointer(clientX, clientY);

      if (!target) {
        setDrop(null);
        return;
      }

      const { group, listId, list } = target;
      const itemElements = list.element.querySelectorAll<HTMLElement>('[data-draggable-list-item]');
      let nextIndex = list.items.length;
      itemElements.forEach((itemElement, index) => {
        const rect = itemElement.getBoundingClientRect();
        if (clientY < rect.top + rect.height / 2 && nextIndex === list.items.length) {
          nextIndex = index;
        }
      });
      setDrop({ group, listId, index: nextIndex });
    };

    const autoScroll = () => {
      const pointer = pointerRef.current;
      if (!pointer) return;

      const target = findListAtPointer(pointer.clientX, pointer.clientY);
      if (!target) {
        autoScrollFrame = null;
        return;
      }

      const rect = target.list.element.getBoundingClientRect();
      const edgeSize = 40;
      let scrollDelta = 0;

      if (pointer.clientY < rect.top + edgeSize && target.list.element.scrollTop > 0) {
        scrollDelta = -Math.min(12, Math.ceil((rect.top + edgeSize - pointer.clientY) / 3));
      } else if (
        pointer.clientY > rect.bottom - edgeSize &&
        target.list.element.scrollTop + target.list.element.clientHeight <
          target.list.element.scrollHeight
      ) {
        scrollDelta = Math.min(12, Math.ceil((pointer.clientY - (rect.bottom - edgeSize)) / 3));
      }

      if (scrollDelta === 0) {
        autoScrollFrame = null;
        return;
      }

      target.list.element.scrollTop += scrollDelta;
      updateDropTarget(pointer.clientX, pointer.clientY);
      autoScrollFrame = requestAnimationFrame(autoScroll);
    };

    const startAutoScroll = () => {
      if (autoScrollFrame == null) autoScrollFrame = requestAnimationFrame(autoScroll);
    };

    const handleMouseMove = (event: globalThis.MouseEvent) => {
      pointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      updateDropTarget(event.clientX, event.clientY);
      startAutoScroll();
    };

    const handleMouseUp = () => {
      if (dropRef.current) {
        const source = listsRef.current.get(`${dragging.group}:${dragging.listId}`);
        const target = listsRef.current.get(`${dropRef.current.group}:${dropRef.current.listId}`);

        if (legacyOnMoveRef.current) {
          legacyOnMoveRef.current({
            itemKey: dragging.itemKey,
            group: dropRef.current.group,
            fromListId: dragging.listId,
            fromIndex: dragging.index,
            toListId: dropRef.current.listId,
            toIndex: dropRef.current.index,
          });
        } else if (source && target) {
          const movedItem = source.items[dragging.index];
          if (movedItem !== undefined) {
            if (source === target) {
              const toIndex =
                dropRef.current.index > dragging.index
                  ? dropRef.current.index - 1
                  : dropRef.current.index;
              const nextItems = [...source.items];
              nextItems.splice(dragging.index, 1);
              nextItems.splice(toIndex, 0, movedItem);
              source.onChange?.(nextItems);
            } else {
              const nextSourceItems = source.items.filter((_, index) => index !== dragging.index);
              const nextTargetItems = [...target.items];
              nextTargetItems.splice(dropRef.current.index, 0, movedItem);
              source.onChange?.(nextSourceItems);
              target.onChange?.(nextTargetItems);
            }
          }
        }
      }
      pointerRef.current = null;
      if (autoScrollFrame != null) cancelAnimationFrame(autoScrollFrame);
      autoScrollFrame = null;
      setDragging(null);
      setDrop(null);
    };

    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        pointerRef.current = null;
        if (autoScrollFrame != null) cancelAnimationFrame(autoScrollFrame);
        autoScrollFrame = null;
        setDragging(null);
        setDrop(null);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
      pointerRef.current = null;
      if (autoScrollFrame != null) cancelAnimationFrame(autoScrollFrame);
    };
  }, [dragging]);

  return (
    <DraggableGroupContext.Provider
      value={{ defaultGroup, dragging, drop, registerList, startDrag }}
    >
      {children}
    </DraggableGroupContext.Provider>
  );
}

export function SDraggableProvider({ children }: SDraggableProviderProps) {
  return <SDraggableProviderInternal>{children}</SDraggableProviderInternal>;
}

export function SDraggableGroup({ children, onMove, ...rest }: SDraggableGroupProps) {
  return (
    <SDraggableProviderInternal defaultGroup="__legacy" legacyOnMove={onMove}>
      <div {...rest}>{children}</div>
    </SDraggableProviderInternal>
  );
}
